/* eslint-disable @next/next/no-img-element */
import { Col, Layout, Row } from "antd";
import { useState, useEffect } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { CloseCircleFilled } from "@ant-design/icons";
import { Header as SundayHeaderV2 } from "sunday-component";
import {
  LOGICAL_GROUP_CHANNEL_CODE,
  RETAIL_LOGICAL_GROUP_CHANNEL_CODE,
} from "src/constants/logicalGroupChannelCode";
import { REGION, REGION_OPTION } from "src/constants/region";
import { ChannelConfigType } from "src/types/channelConfig";
import dynamic from "next/dynamic";
import { User } from "src/types/user";
import { down } from "styled-breakpoints";
import { FOOTER_TYPE, HEADER_TYPE } from "src/constants/layout";
import Footer from "../Common/Footer/Footer";
import { HeaderType, Language } from "../Common/Header/IHeader";
import RetailFooter from "../Common/Footer/RetailFooter";
import Header from "../Common/Header";
import { LineBubble } from "./LineBubble";

const { Content } = Layout;

const Container = styled(Layout)`
  min-height: 100vh;
  background: var(--ui-form-background_page-background-color);

  .announcement-wrapper {
    background-image: url("/static/announcement-background.png");
    height: 160px;
    overflow: hidden;
    background-repeat: no-repeat;
    background-size: cover;

    .announcement-wording {
      padding-left: 7vw;
      padding-top: 1.75em;
      font-family: "JUST Sans", "Noto Sans Thai", -apple-system,
        BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell,
        Fira Sans, Droid Sans, Helvetica Neue, sans-serif;

      .announcement-title {
        font-weight: 700;
        font-size: 28px;
        color: #202123;
      }

      .desc1,
      .desc2 {
        font-size: 22px;
        color: #5f6468;
      }
    }

    .announcement-logo {
      padding-top: 2.5em;

      img {
        display: block;
        max-width: 100%;
        height: auto;
      }
    }

    .announcement-close {
      text-align: right;
      padding: 12px 16px 0 0;
      font-size: 26px;
      color: white;
    }

    ${down("md")} {
      height: auto;
      min-height: 160px;

      > .ant-row {
        position: relative;
      }

      .announcement-wording {
        padding-top: 24px;
        padding-bottom: 24px;

        .announcement-title {
          margin-bottom: 4px;
          font-size: 24px;
          line-height: 1.35;
        }

        .desc1,
        .desc2 {
          font-size: 16px;
          line-height: 1.5;
        }
      }

      .announcement-logo {
        padding: 40px 8px 16px 0;

        img {
          filter: drop-shadow(0 2px 4px rgba(32, 45, 120, 0.55));
        }
      }

      .announcement-close {
        position: absolute;
        top: 10px;
        right: 12px;
        z-index: 1;
        padding: 0;
        font-size: 24px;
      }
    }
  }
`;

const CustomContent = styled(Content)<{ contentBackgroundColor?: string }>`
  min-height: 66.67vh;
  ${({ contentBackgroundColor }) =>
    contentBackgroundColor && `background: ${contentBackgroundColor};`}
  ${down("md")} {
    min-height: fit-content;
  }
`;

const CookieConsent = dynamic(() => import("../CookieConsent/CookieConsent"), {
  ssr: false,
  loading: () => <div />,
});

const SundayIndoFooter = dynamic(
  () => import("sunday-component").then((mod) => mod.IndoFooter),
  {
    loading: () => <div />,
  }
);

export type WithLayoutPropType = {
  channelConfig: ChannelConfigType;
  children: JSX.Element;
  headerType: HeaderType;
  footerType: string;
  user?: User;
  contentBackgroundColor?: string;
  isShowFooter?: boolean;
  isShowAnnouncement?: boolean;
};

export const withLayout = (WrappedComponent) => {
  return (props: WithLayoutPropType) => {
    const {
      channelConfig,
      headerType = HEADER_TYPE.MENU,
      footerType = FOOTER_TYPE.MENU,
      user,
      contentBackgroundColor,
      isShowFooter = true,
      isShowAnnouncement = true,
    } = props;
    const { content } = channelConfig;
    const router = useRouter();
    const { locale } = router;
    const isUseSundayLayout =
      LOGICAL_GROUP_CHANNEL_CODE === RETAIL_LOGICAL_GROUP_CHANNEL_CODE ||
      !content;
    const onChangeLanguage = (language: any) => {
      if (language !== router.locale) {
        router.replace(router.asPath, router.asPath, { locale: language });
      }
    };
    const availableLanguages = content?.supportedLangs.map((lang) => lang.code);
    const [footer, setFooter] = useState(false);
    const [showAnnouncement, setShowAnnouncement] = useState(true);
    const { t } = useTranslation("layout");

    useEffect(() => {
      setTimeout(() => {
        setFooter(isShowFooter);
      }, 800);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dev-note: to prevent /default/404, /default/500 pre-render error on build time
    if (locale === "default") {
      return (
        <CustomContent contentBackgroundColor={contentBackgroundColor}>
          <WrappedComponent {...props} />
        </CustomContent>
      );
    }
    return (
      <>
        <Container>
          {content?.askUsChatbot?.enabled && <LineBubble />}
          {!isUseSundayLayout && (
            <>
              <Header
                showSwitchLanguage
                type="partner"
                onChangeLanguage={onChangeLanguage}
                language={locale as Language}
                theme="white" // 'orange' | 'white' | 'yellow' | 'grey' | 'coolGreyAndWhite'
                content={content}
              />
              <CustomContent contentBackgroundColor={contentBackgroundColor}>
                <WrappedComponent {...props} />
              </CustomContent>
              {!!footer && <Footer content={content} />}
            </>
          )}
          {isUseSundayLayout && (
            <>
              {REGION === REGION_OPTION.TH && (
                <>
                  {headerType === HEADER_TYPE.PROFILE && (
                    <Header
                      showSwitchLanguage
                      type={HEADER_TYPE.PROFILE}
                      onChangeLanguage={onChangeLanguage}
                      language={locale as Language}
                      content={content}
                      isLogin={!!user}
                      profile={user?.profile}
                    />
                  )}
                  {headerType === HEADER_TYPE.MENU && (
                    <Header
                      showSwitchLanguage
                      type={HEADER_TYPE.MENU}
                      onChangeLanguage={onChangeLanguage}
                      language={locale as Language}
                      content={content}
                    />
                  )}
                  {headerType === HEADER_TYPE.SIMPLE && (
                    <Header
                      showSwitchLanguage
                      type={HEADER_TYPE.SIMPLE}
                      onChangeLanguage={onChangeLanguage}
                      language={locale as Language}
                      content={content}
                    />
                  )}
                </>
              )}
              {REGION === REGION_OPTION.ID && (
                <SundayHeaderV2
                  showSwitchLanguage
                  type="indo"
                  onChangeLanguage={onChangeLanguage}
                  language={locale as Language}
                  theme="white"
                  availableLanguages={availableLanguages}
                />
              )}

              {isShowAnnouncement && showAnnouncement && (
                <div className="announcement-wrapper">
                  <Row onClick={() => setShowAnnouncement(false)}>
                    <Col span={16} className="announcement-wording">
                      <Row className="announcement-title">
                        {t("announcement.title")}
                      </Row>
                      <Row className="announcement-desc1">
                        {t("announcement.desc1")}
                      </Row>
                      <Row className="announcement-d2sc1">
                        {t("announcement.desc2")}
                      </Row>
                    </Col>
                    <Col span={7} className="announcement-logo">
                      <img
                        src="/static/company-logo/logo-icth.svg"
                        alt="logo"
                      />
                    </Col>
                    <Col span={1} className="announcement-close">
                      <CloseCircleFilled />
                    </Col>
                  </Row>
                </div>
              )}

              <CustomContent contentBackgroundColor={contentBackgroundColor}>
                <WrappedComponent {...props} />
              </CustomContent>

              {REGION === REGION_OPTION.TH && footer && (
                <RetailFooter content={content} footerType={footerType} />
              )}
              {REGION === REGION_OPTION.ID && footer && (
                <SundayIndoFooter company="ID-SDI" />
              )}
              <CookieConsent />
            </>
          )}
        </Container>
      </>
    );
  };
};
